/** Ana ekran: öne çıkan devam banner'ı, kişisel raflar, kütüphaneler. */

import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import {
  api,
  type Capabilities,
  type HomeFeed,
  type HomeRail,
  type Item,
  type Library,
} from '../api.ts';
import { HeroBanner } from '../components/HeroBanner.tsx';
import { ItemCard } from '../components/ItemCard.tsx';
import { Loading } from '../components/States.tsx';
import { formatDate } from '../format.ts';
import {
  ArrowRightIcon,
  BookmarkIcon,
  ClockIcon,
  FilmIcon,
  FolderIcon,
  LockIcon,
  MusicIcon,
  PlayIcon,
  PlusIcon,
  SparklesIcon,
  TvIcon,
} from '../Icon.tsx';
import { resizeProxyImage } from '../imageSize.ts';
import { mediaUrl } from '../mediaToken.ts';
import { loadOfflineCatalog, saveOfflineHome } from '../offlineCatalog.ts';
import { useSession } from '../session.tsx';

interface LibraryMeta {
  /** Kütüphane kartındaki ikon (emoji değil, aileden SVG). */
  icon: ReactNode;
  themeClass: string;
  label: string;
}

function getLibraryMeta(kind: string, name: string): LibraryMeta {
  const lower = name.toLowerCase();
  if (
    lower.includes('müzik') ||
    lower.includes('muzik') ||
    lower.includes('music') ||
    kind === 'music'
  ) {
    return { icon: <MusicIcon size={20} />, themeClass: 'library-card--music', label: 'Müzik' };
  }
  if (kind === 'movie' || lower.includes('film')) {
    return { icon: <FilmIcon size={20} />, themeClass: 'library-card--movie', label: 'Film' };
  }
  if (kind === 'show' || lower.includes('dizi') || lower.includes('series')) {
    return { icon: <TvIcon size={20} />, themeClass: 'library-card--show', label: 'Dizi' };
  }
  return { icon: <FolderIcon size={20} />, themeClass: 'library-card--default', label: 'Medya' };
}

/**
 * Öğe bir müzik kütüphanesine mi ait?
 *
 * Müzik parçaları veritabanında `kind: 'movie'` olarak tutulur; tür ayrımı
 * kütüphanenin `kind`/adından gelir. "Listem" ve "Son Eklenenler" raflarında
 * kare kapak ve "Müzik" rozeti için bu bilgi gerekir.
 */
function isMusicItem(item: Item, libraries: Library[] | null): boolean {
  const library = libraries?.find((entry) => entry.id === item.libraryId);
  if (!library) return false;
  const lower = library.name.toLowerCase();
  return (
    library.kind === 'music' ||
    lower.includes('müzik') ||
    lower.includes('muzik') ||
    lower.includes('music')
  );
}

export function HomeScreen({
  libraries,
  onOpenLibrary,
  onWatch,
  onAddLibrary,
}: {
  capabilities?: Capabilities | null;
  /** Periyodik tazelenen kütüphane listesi (ADR-011 erişilebilirliğiyle). */
  libraries: Library[] | null;
  onOpenLibrary: (libraryId: string) => void;
  onWatch: (itemId: string) => void;
  /** Yönetici ise boş durumda "İlk kütüphaneni ekle" eylemi. */
  onAddLibrary?: (() => void) | undefined;
}): ReactNode {
  /**
   * V6-06: ana sayfa tek `/api/me/home` isteğiyle gelir. Raflar sunucuda
   * sıralanır, görünürlük uygulanır ve içerik tekrarı ayıklanır.
   */
  const { user } = useSession();
  const [feed, setFeed] = useState<HomeFeed | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void api
      .home()
      .then((result) => {
        if (!cancelled) {
          setFeed(result);
          setLoading(false);
          if (user !== null) void saveOfflineHome(user.id, result);
        }
      })
      .catch(() => {
        if (cancelled) return;
        if (user === null) {
          setFeed({ hero: null, rails: [] });
          setLoading(false);
          return;
        }
        void loadOfflineCatalog(user.id).then((snapshot) => {
          if (cancelled) return;
          setFeed(snapshot?.home ?? { hero: null, rails: [] });
          setLoading(false);
        });
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const featured = feed?.hero ?? null;
  const rails = feed?.rails ?? [];

  const railIcon = (rail: HomeRail): ReactNode => {
    switch (rail.id) {
      case 'continue':
        return <PlayIcon size={15} />;
      case 'recent':
        return <SparklesIcon size={15} />;
      case 'watchlist':
        return <BookmarkIcon size={15} />;
      case 'collections':
        return <FolderIcon size={15} />;
      case 'history':
        return <ClockIcon size={15} />;
    }
  };

  const renderRailCard = (item: Item): ReactNode => {
    const isMusic = isMusicItem(item, libraries);
    const playable = item.kind === 'movie' || item.kind === 'episode';
    return (
      <ItemCard
        key={item.id}
        item={item}
        layout={isMusic ? 'square' : 'portrait'}
        mediaKind={isMusic ? 'music' : 'video'}
        href={playable ? `#/izle/${item.id}` : `#/oge/${item.id}`}
        onClick={playable ? () => onWatch(item.id) : undefined}
      />
    );
  };

  const railSections = rails.map((rail) => (
    <section key={rail.id} className="section">
      <div className="section__header">
        <div className="section__header-title">
          <span className="section__header-icon">{railIcon(rail)}</span>
          <h2 className="section__title">{rail.title}</h2>
        </div>
        <span className="section__badge">{rail.items.length} içerik</span>
      </div>

      {rail.id === 'continue' ? (
        <div className="grid grid--continue">
          {rail.items.map((item) => {
            const isMusic = isMusicItem(item, libraries);
            return (
              <ItemCard
                key={item.id}
                item={item}
                layout="landscape"
                mediaKind={isMusic ? 'music' : 'video'}
                href={`#/izle/${item.id}`}
                onClick={() => onWatch(item.id)}
              />
            );
          })}
        </div>
      ) : (
        <div className="grid">{rail.items.map(renderRailCard)}</div>
      )}
    </section>
  ));

  return (
    <div className="home">
      {/* 1. Öne çıkan sinematik hero banner */}
      {featured && (
        <HeroBanner
          key={featured.id}
          item={featured}
          mediaKind={isMusicItem(featured, libraries) ? 'music' : 'video'}
          onWatch={() => onWatch(featured.id)}
        />
      )}

      {/* 2. Kişisel raf yoksa soğuk kullanıcı için anlamlı karşılama */}
      {!loading && featured === null && rails.length === 0 && (
        <section className="section">
          <div className="hero hero--welcome">
            <div className="hero__content">
              <span className="hero__badge">Hoş Geldiniz</span>
              <h2 className="hero__title">Kütüphanenizi Keşfetmeye Başlayın</h2>
              <p className="hero__meta" style={{ maxWidth: '500px', lineHeight: 1.6 }}>
                Henüz kişisel bir raf oluşturacak kadar izleme geçmişi yok. Aşağıdaki
                kütüphanelerden bir film veya dizi seçerek başlayabilirsiniz.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* 3. Sunucudan sıralanmış, tekrarsız kişisel raflar (V6-06) */}
      {railSections}

      {/* 4. Kütüphaneler bölümü */}
      <section className="section">
        <div className="section__header">
          <div className="section__header-title">
            <span className="section__header-icon">
              <FolderIcon size={17} />
            </span>
            <h2 className="section__title">Kütüphaneleriniz</h2>
          </div>
          {libraries && libraries.length > 0 && (
            <span className="section__badge">{libraries.length} kütüphane</span>
          )}
        </div>

        {libraries === null && <Loading />}
        {libraries && libraries.length === 0 && (
          <div className="home-empty">
            <p className="home-empty__text">Henüz kütüphane tanımlanmadı.</p>
            <p className="home-empty__hint">
              Film, dizi veya müzik klasörünüzü ekleyerek başlayın. Desteklenen biçimler: MKV, MP4,
              MP3, FLAC.
            </p>
            {onAddLibrary && (
              <button
                type="button"
                className="button button--primary home-empty__action"
                onClick={onAddLibrary}
              >
                <PlusIcon size={16} />
                İlk kütüphaneni ekle
              </button>
            )}
          </div>
        )}
        {libraries !== null && libraries.length > 0 && (
          <div className="libraries">
            {libraries.map((library) => {
              const meta = getLibraryMeta(library.kind, library.name);
              const previewLocal = library.previewThumbnailUrl
                ? library.previewThumbnailUrl
                : library.previewItemId
                  ? mediaUrl(`/api/items/${library.previewItemId}/thumbnail`)
                  : null;
              return (
                <button
                  key={library.id}
                  type="button"
                  className={`library-card ${meta.themeClass} ${library.reachable ? '' : 'library-card--down'}`}
                  onClick={() => onOpenLibrary(library.id)}
                  title={
                    library.reachable
                      ? undefined
                      : `Erişilemiyor: ${library.unavailableReason ?? 'bilinmiyor'}`
                  }
                >
                  <div className="library-card__icon-wrapper" aria-hidden="true">
                    <span className="library-card__icon">{meta.icon}</span>
                  </div>

                  <div className="library-card__content">
                    <div className="library-card__header">
                      <span className="library-card__name">{library.name}</span>
                      {library.layer === 'cold' && (
                        <span
                          className="badge badge--cold"
                          title="Evdeki bilgisayardan, tünel üzerinden"
                        >
                          <LockIcon size={12} />
                          Tünel
                        </span>
                      )}
                      {!library.reachable && (
                        <span className="badge badge--down">Erişilemiyor</span>
                      )}
                    </div>

                    <div className="library-card__footer">
                      <span className="library-card__count-pill">{library.itemCount} içerik</span>
                      {library.scannedAt !== null && (
                        <span className="library-card__scanned">
                          {formatDate(library.scannedAt)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* D-16: kütüphanedeki en yeni içerikten bir iz. */}
                  {previewLocal !== null && (
                    <span className="library-card__preview" aria-hidden="true">
                      <img
                        src={resizeProxyImage(library.previewPosterUrl, 'w342') ?? previewLocal}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        onError={(event) => {
                          const image = event.currentTarget;
                          if (library.previewPosterUrl && !image.src.endsWith(previewLocal)) {
                            image.src = previewLocal;
                            return;
                          }
                          const wrapper = image.parentElement;
                          if (wrapper) wrapper.style.display = 'none';
                        }}
                      />
                    </span>
                  )}

                  <span className="library-card__arrow" aria-hidden="true">
                    <ArrowRightIcon size={16} />
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
